import React, { useEffect, useMemo, useState } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import TodayIcon from '@mui/icons-material/Today';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IPreviewPageOption } from '../../utils/previewPages';
import { formatPreviewDate, parsePreviewDateInput, TPreviewDateParts, toPreviewDateInput } from '../../utils/previewDates';

interface PreviewLinkAdvancedSettingsProps {
  allowComments: boolean;
  privateComments: boolean;
  expirationEnabled: boolean;
  expirationValue: string;
  pageOptions: IPreviewPageOption[];
  startPageEnabled: boolean;
  startPageId: number | null;
  onAllowCommentsChange: (value: boolean) => void;
  onPrivateCommentsChange: (value: boolean) => void;
  onExpirationEnabledChange: (value: boolean) => void;
  onExpirationValueChange: (value: string) => void;
  onStartPageEnabledChange: (value: boolean) => void;
  onStartPageIdChange: (value: number | null) => void;
  title?: string | null;
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function toUtcMonthStart(parts: TPreviewDateParts): Date {
  return new Date(Date.UTC(parts.year, parts.month - 1, 1));
}

function getTodayMonthStart(): Date {
  const today = new Date();
  return new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
}

function getCurrentDateValue(): string {
  const today = new Date();
  const target = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999));
  return toPreviewDateInput(target.toISOString());
}

function shiftUtcMonth(date: Date, offset: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1));
}

function buildCalendarDays(viewMonth: Date): Array<TPreviewDateParts | null> {
  const year = viewMonth.getUTCFullYear();
  const month = viewMonth.getUTCMonth();
  const firstDay = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const leadingEmptySlots = (firstDay.getUTCDay() + 6) % 7;

  const items: Array<TPreviewDateParts | null> = [];
  for (let index = 0; index < leadingEmptySlots; index++) {
    items.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    items.push({ year, month: month + 1, day });
  }

  while (items.length % 7 !== 0) {
    items.push(null);
  }

  return items;
}

function isSamePreviewDate(a?: TPreviewDateParts | null, b?: TPreviewDateParts | null): boolean {
  if (!a || !b) {
    return false;
  }

  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function formatMonthLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function formatPreviewDateParts(parts: TPreviewDateParts): string {
  return `${`${parts.day}`.padStart(2, '0')}/${`${parts.month}`.padStart(2, '0')}/${parts.year}`;
}

function PreviewDatePickerField({
  disabled,
  label,
  value,
  onChange,
}: {
  disabled: boolean;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewMonth, setViewMonth] = useState<Date>(getTodayMonthStart());

  const parsedValue = useMemo(() => parsePreviewDateInput(value), [value]);
  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (parsedValue) {
      setViewMonth(toUtcMonthStart(parsedValue));
    }
  }, [isOpen, parsedValue]);

  useEffect(() => {
    if (!disabled) {
      return;
    }

    setAnchorEl(null);
  }, [disabled]);

  const openCalendar = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    setAnchorEl(event.currentTarget);
    setViewMonth(parsedValue ? toUtcMonthStart(parsedValue) : getTodayMonthStart());
  };

  const closeCalendar = () => setAnchorEl(null);

  const selectDay = (day: TPreviewDateParts) => {
    onChange(formatPreviewDateParts(day));
    setAnchorEl(null);
  };

  const selectToday = () => {
    const nextValue = getCurrentDateValue();
    onChange(nextValue);
    const nextParts = parsePreviewDateInput(nextValue);
    if (nextParts) {
      setViewMonth(toUtcMonthStart(nextParts));
    }
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        fullWidth
        size='small'
        placeholder='DD/MM/YYYY'
        InputLabelProps={{ shrink: true }}
        inputProps={{ inputMode: 'numeric', placeholder: 'DD/MM/YYYY' }}
        helperText='DD/MM/YYYY'
        error={value.trim() !== '' && !parsedValue}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton onClick={openCalendar} disabled={disabled} edge='end' aria-label='Open calendar picker'>
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, width: 320 } }}
      >
        <Stack spacing={1.25}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
            <IconButton size='small' onClick={() => setViewMonth((current) => shiftUtcMonth(current, -1))} aria-label='Previous month'>
              <ChevronLeftIcon fontSize='small' />
            </IconButton>
            <Typography variant='subtitle2' fontWeight={700}>
              {formatMonthLabel(viewMonth)}
            </Typography>
            <IconButton size='small' onClick={() => setViewMonth((current) => shiftUtcMonth(current, 1))} aria-label='Next month'>
              <ChevronRightIcon fontSize='small' />
            </IconButton>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
            {WEEKDAY_LABELS.map((label) => (
              <Typography key={label} variant='caption' color='text.secondary' align='center' sx={{ py: 0.5 }}>
                {label}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
            {calendarDays.map((day, index) =>
              day ? (
                <ButtonBase
                  key={`${day.year}-${day.month}-${day.day}`}
                  onClick={() => selectDay(day)}
                  sx={{
                    borderRadius: 1,
                    minHeight: 36,
                    px: 0.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: isSamePreviewDate(day, parsedValue) ? 'primary.main' : 'divider',
                    backgroundColor: isSamePreviewDate(day, parsedValue) ? 'primary.main' : 'transparent',
                    color: isSamePreviewDate(day, parsedValue) ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant='body2' component='span'>
                    {day.day}
                  </Typography>
                </ButtonBase>
              ) : (
                <Box key={`empty-${index}`} sx={{ minHeight: 36 }} />
              ),
            )}
          </Box>

          <Stack direction='row' spacing={1} justifyContent='space-between'>
            <Button size='small' startIcon={<TodayIcon />} onClick={selectToday}>
              Today
            </Button>
            <Button
              size='small'
              startIcon={<ClearIcon />}
              onClick={() => {
                onChange('');
                setAnchorEl(null);
              }}
              disabled={value.trim() === ''}
            >
              Clear
            </Button>
          </Stack>

          {parsedValue && (
            <Typography variant='caption' color='text.secondary'>
              Selected: {formatPreviewDate(value)}
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
}

export default function PreviewLinkAdvancedSettings({
  allowComments,
  privateComments,
  expirationEnabled,
  expirationValue,
  pageOptions,
  startPageEnabled,
  startPageId,
  onAllowCommentsChange,
  onPrivateCommentsChange,
  onExpirationEnabledChange,
  onExpirationValueChange,
  onStartPageEnabledChange,
  onStartPageIdChange,
  title = 'Advanced Settings',
}: PreviewLinkAdvancedSettingsProps) {
  return (
    <Stack spacing={2}>
      {title ? <Typography variant='h6'>{title}</Typography> : null}

      <Box>
        <FormControlLabel control={<Checkbox checked={allowComments} onChange={(event) => onAllowCommentsChange(event.target.checked)} />} label='Allow comments' />
        <Box sx={{ pl: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={privateComments}
                disabled={!allowComments}
                onChange={(event) => onPrivateCommentsChange(event.target.checked)}
              />
            }
            label='Private comments'
          />
        </Box>
      </Box>

      <Box>
        <FormControlLabel
          control={<Checkbox checked={expirationEnabled} onChange={(event) => onExpirationEnabledChange(event.target.checked)} />}
          label='Expiration date for preview link'
        />
        <Box sx={{ pl: 4, pt: 1, maxWidth: 360 }}>
          <PreviewDatePickerField label='Expiration date' value={expirationValue} onChange={onExpirationValueChange} disabled={!expirationEnabled} />
        </Box>
      </Box>

      <Box>
        <FormControlLabel
          control={<Checkbox checked={startPageEnabled} onChange={(event) => onStartPageEnabledChange(event.target.checked)} disabled={pageOptions.length === 0} />}
          label='Start preview from this page'
        />
        <Box sx={{ pl: 4, pt: 1, maxWidth: 360 }}>
          <TextField
            select
            label='Page name'
            value={startPageId === null ? '' : `${startPageId}`}
            onChange={(event) => {
              const nextValue = event.target.value;
              onStartPageIdChange(nextValue === '' ? null : Number.parseInt(String(nextValue), 10));
            }}
            disabled={!startPageEnabled || pageOptions.length === 0}
            fullWidth
            size='small'
          >
            <MenuItem value=''>
              <em>{pageOptions.length === 0 ? 'No pages available' : 'Select a page'}</em>
            </MenuItem>
            {pageOptions.map((page) => (
              <MenuItem key={page.id} value={`${page.id}`}>
                <Box sx={{ pl: page.depth * 2 }}>{page.label}</Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Stack>
  );
}
